/**
 * 精读页的 Markdown 导出：**文章 + 我的标注 + 题目与答案**。
 *
 * 导出的文档定位是"精读笔记"：回看时要能一口气读完整篇文章、看到当时圈出来的地方，
 * 走的时候也不用手忙脚乱地回去翻题目和答案——所以题目、选项、正确答案一并附在文末。
 * 题目区（题干/选项）上的标注不会丢，它们放在「题目区标注」里。
 *
 * 标注在正文里的表达方式：
 *   · 高亮 → `==文字==`（Obsidian / Typora 都认这个语法）
 *   · 笔记 → `[^n]` 脚注，文末按顺序列出
 */
import { KIND_LABELS } from '@leits/shared/client';
import { sentenceRanges } from './annotate.js';
/** 题干/说明是 HTML，转成纯文本；<strong> 保留成 Markdown 的加粗。 */
function htmlToText(html) {
    if (!html)
        return '';
    const container = document.createElement('div');
    container.innerHTML = html.replace(/<strong>/g, '**').replace(/<\/strong>/g, '**');
    return (container.textContent ?? '').replace(/\s+/g, ' ').trim();
}
/** 整组共用的选项池（匹配题、标题题每个题号都挂着一份相同的选项） */
function sharedOptions(questions) {
    if (questions.length === 0)
        return null;
    const first = questions[0]?.options ?? [];
    if (first.length === 0)
        return null;
    const identical = questions.every((question) => (question.options ?? []).length === first.length &&
        (question.options ?? []).every((option, index) => option.key === first[index]?.key));
    return identical ? first : null;
}
/** 段落纯文本：与 DOM 里的标注坐标系保持一致（连续空白折叠成一个空格）。 */
export function blockPlainText(html) {
    const container = document.createElement('div');
    container.innerHTML = html;
    return (container.textContent ?? '').replace(/\s+/g, ' ').trim();
}
/**
 * 把一条标注换算成段落里的绝对字符区间。
 * 锚点是「句序 + 句内偏移」，所以先按句界算出该句在段落里的起点。
 */
function locate(annotation, ranges) {
    if (ranges.length === 0)
        return null;
    const range = ranges[Math.min(annotation.sentenceIndex, ranges.length - 1)];
    if (!range)
        return null;
    let start = range.start + annotation.startOffset;
    let end = range.start + annotation.endOffset;
    // 偏移为空或反了，按"整句"处理（与页面上的渲染规则一致）
    if (end <= start) {
        start = range.start;
        end = range.end;
    }
    start = Math.max(range.start, Math.min(start, range.end));
    end = Math.max(start, Math.min(end, range.end));
    return { start, end };
}
/** 给一段正文套上高亮与脚注标记。 */
function decorateParagraph(text, annotations, nextFootnote) {
    if (annotations.length === 0)
        return { text, used: [], skipped: [] };
    const ranges = sentenceRanges(text);
    const placed = [];
    const skipped = [];
    for (const annotation of annotations) {
        const span = locate(annotation, ranges);
        if (!span) {
            skipped.push(annotation);
            continue;
        }
        placed.push({ start: span.start, end: span.end, annotation, footnote: null });
    }
    placed.sort((a, b) => a.start - b.start);
    // 重叠的标注没法都用 == == 表达（会互相切断），保留靠前的，其余退到文末清单
    const accepted = [];
    let cursor = -1;
    for (const item of placed) {
        if (item.start < cursor) {
            skipped.push(item.annotation);
            continue;
        }
        accepted.push(item);
        cursor = item.end;
    }
    // 脚注编号按正文出现顺序给，别按锚点 id 的字典序——否则文末清单会和正文对不上
    for (const item of accepted) {
        if (item.annotation.note)
            item.footnote = nextFootnote();
    }
    // 从后往前插入，前面还没处理的偏移就不会被影响
    let result = text;
    for (const item of [...accepted].reverse()) {
        const inner = result.slice(item.start, item.end);
        const wrapped = item.annotation.color ? `==${inner}==` : inner;
        const marker = item.footnote === null ? '' : `[^${item.footnote}]`;
        result = `${result.slice(0, item.start)}${wrapped}${marker}${result.slice(item.end)}`;
    }
    return { text: result, used: accepted, skipped };
}
function paragraphLabel(blockId) {
    return /^p-([A-Z])$/.exec(blockId)?.[1] ?? null;
}
function describe(annotation) {
    const color = annotation.color ? `${annotation.color} 色高亮` : '纯笔记';
    const text = annotation.selectedText ? ` \`${annotation.selectedText}\`` : '';
    const note = annotation.note ? ` — ${annotation.note}` : '';
    return `${color}${text}${note}`;
}
function anchorLabel(annotation) {
    const [owner, option] = annotation.anchorId.split('#');
    return option ? `${owner} 的选项 ${option}` : (owner ?? annotation.anchorId);
}
export function buildMarkdown(exam, annotations) {
    const passageAnnotations = annotations.filter((item) => item.anchorKind === 'passage');
    const questionAnnotations = annotations.filter((item) => item.anchorKind === 'question');
    let footnoteCount = 0;
    const nextFootnote = () => {
        footnoteCount += 1;
        return footnoteCount;
    };
    const article = [];
    const footnotes = [];
    const skippedNotes = [];
    for (const block of exam.passage.blocks) {
        const label = paragraphLabel(block.id);
        const prefix = label ? `**${label}** ` : '';
        const mine = passageAnnotations.filter((item) => item.anchorId === block.id);
        const { text, used, skipped } = decorateParagraph(blockPlainText(block.html), mine, nextFootnote);
        article.push(`${prefix}${text}`);
        for (const item of used) {
            if (item.footnote === null)
                continue;
            footnotes.push(`[^${item.footnote}]: **${label ? `段落 ${label}` : block.id} · 第 ${item.annotation.sentenceIndex + 1} 句** ${describe(item.annotation)}`);
        }
        for (const item of skipped) {
            skippedNotes.push(`- **${label ? `段落 ${label}` : block.id}**：${describe(item)}`);
        }
    }
    const numbers = exam.questions.map((question) => question.displayNumber);
    const bounds = numbers.length > 0 ? `${numbers[0]}–${numbers[numbers.length - 1]}` : '';
    const lines = [];
    lines.push(`# ${exam.meta.title ?? exam.examId}`, '');
    lines.push(`> ${exam.examId} · ${exam.meta.category} · 卷面题号 ${bounds}${exam.meta.sourceRefs?.pdf ? ` · 来源：${exam.meta.sourceRefs.pdf}` : ''}`);
    lines.push(`> 导出时间：${new Date().toLocaleString('zh-CN')}`);
    lines.push('', '---', '', '## 文章', '');
    for (const paragraph of article)
        lines.push(paragraph, '');
    lines.push('---', '', '## 我的标注', '');
    if (footnotes.length === 0 && skippedNotes.length === 0 && questionAnnotations.length === 0) {
        lines.push('_还没有标注。在精读页里选中文字即可高亮或写笔记。_', '');
    }
    if (footnotes.length > 0)
        lines.push(...footnotes, '');
    if (skippedNotes.length > 0) {
        lines.push('### 没能嵌入正文的标注', '', ...skippedNotes, '');
    }
    if (questionAnnotations.length > 0) {
        lines.push('### 题目区标注', '');
        for (const item of questionAnnotations) {
            lines.push(`- **${anchorLabel(item)}** 第 ${item.sentenceIndex + 1} 句：${describe(item)}`);
        }
        lines.push('');
    }
    /* ---------------- 题目与答案 ---------------- */
    if (exam.questions.length > 0) {
        const byId = new Map(exam.questions.map((question) => [question.id, question]));
        lines.push('---', '', '## 题目与答案', '');
        for (const group of exam.questionGroups) {
            const members = group.questionIds
                .map((id) => byId.get(id))
                .filter((question) => Boolean(question));
            if (members.length === 0)
                continue;
            const first = members[0]?.displayNumber ?? 0;
            const last = members[members.length - 1]?.displayNumber ?? first;
            const range = first === last ? `第 ${first} 题` : `第 ${first}–${last} 题`;
            lines.push(`### ${KIND_LABELS[group.kind] ?? group.kind}（${range}）`, '');
            const instructions = htmlToText(group.instructionsHtml);
            if (instructions)
                lines.push(`> ${instructions}`, '');
            // 匹配/标题题的选项全组共用，只在组里列一次，避免每题重复一大坨
            const pool = sharedOptions(members);
            if (pool) {
                lines.push('**选项**', '');
                for (const option of pool)
                    lines.push(`- **${option.key}.** ${option.text}`);
                lines.push('');
            }
            for (const question of members) {
                lines.push(`**${question.displayNumber}.** ${htmlToText(question.promptHtml)}`);
                const own = question.options ?? [];
                if (!pool && own.length > 0) {
                    for (const option of own)
                        lines.push(`   - **${option.key}.** ${option.text}`);
                }
                const accepted = (question.answer.accepted ?? []).join(' / ');
                lines.push(`   - ✅ 答案：**${accepted}**`, '');
            }
        }
    }
    return {
        markdown: `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd()}\n`,
        inlineCount: footnotes.length,
        listOnlyCount: skippedNotes.length + questionAnnotations.length,
    };
}
/** 触发浏览器下载。 */
export function downloadMarkdown(filename, markdown) {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // 立刻回收会让部分浏览器来不及开始下载，延后一点
    window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}
/** 文件名里不能出现的字符统一换掉。 */
export function safeFilename(name) {
    return name.replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();
}
//# sourceMappingURL=markdownExport.js.map